import {
  type ArrayElement, notEmpty,
} from '~/_shared/utils/array/array.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { BoundaryEditAction } from '~/boundary/editMode/boundaryEditModal/boundaryEditModal.helpers';
import type { BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import { BOUNDARY_TERRITORY_GROUP_REMOVE_SUCCESS } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import {
  boundarySelectQuickEditCustomBoundaryOpenModal, selectedBucketChanged,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { getSelectionColor } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.utils';
import type { MapSettingsToolsStateBoundaryStateAction } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.action';
import { activeBTGIdPerBoundaryGroupChanged } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { type BoundarySelectAction } from './boundarySelect.action';
import {
  BOUNDARY_SELECT_ADD_BOUNDARY, BOUNDARY_SELECT_EDIT_ACTIVATE, BOUNDARY_SELECT_EDIT_BOUNDARY_TERRITORY_OPEN_MODAL,
  BOUNDARY_SELECT_EDIT_CLOSE_MODAL, BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_OPEN_MODAL,
  BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_SUBMIT_OPEN_MODAL, BOUNDARY_SELECT_EDIT_DEACTIVATE, BOUNDARY_SELECT_EDIT_RESET,
  BOUNDARY_SELECT_HOVER_BOUNDARY, BOUNDARY_SELECT_OPTIMIZED_TERRITORIES_SUBSET_OPEN_MODAL,
  BOUNDARY_SELECT_REMOVE_BOUNDARY, BOUNDARY_SELECT_SET_LASSO_IS_LOADING, BOUNDARY_SELECT_SET_LASSO_OVERLAP,
  BOUNDARY_SELECT_SET_SELECT_TYPE, BOUNDARY_SELECT_STOP_BOUNDARY_HOVER,
  BOUNDARY_SELECT_WMS_OPTIMIZED_TERRITORIES_SUBSET_OPEN_MODAL,
} from './boundarySelect.actionTypes';
import {
  type BoundarySelectInactiveState, BoundarySelectMode, type BoundarySelectState,
  BoundarySelectType, isBoundarySelectActive, isBoundarySelectQuickEditCustomBoundary, SelectLassoOverlap,
} from './boundarySelect.state';

export const defaultBoundarySelectOptions = {
  selectType: BoundarySelectType.Click,
  selectLassoOverlap: SelectLassoOverlap.Partial,
  isLassoSelectLoading: false,
  hoveredBoundaryId: null,
  allowMultipleSelection: true,
} as const;

const initialState: BoundarySelectInactiveState = {
  isModalOpen: false,
  isActive: false,
  selectBoundaryGroupId: null,
  selectionModeData: null,
  selectedBoundaryInfos: null,
  onClose: noop,
  onSubmit: noop,
  ...defaultBoundarySelectOptions,
};

export const boundarySelectReducer = (
  state: BoundarySelectState = initialState,
  action: BoundarySelectAction | MapSettingsToolsStateBoundaryStateAction | BoundaryTerritoryGroupsAction,
): BoundarySelectState => {
  if (activeBTGIdPerBoundaryGroupChanged.match(action)) {
    // If active BTG for selected boundary group changes, we close the modal.
    if (state.selectBoundaryGroupId === action.payload.boundaryGroupId) {
      return initialState;
    }
    return state;
  }

  if (selectedBucketChanged.match(action)) {
    if (isBoundarySelectQuickEditCustomBoundary(state)) {
      const { selectedBucketId } = action.payload;

      const nextSelectType = (state.selectType === BoundarySelectType.HoverActive) ? BoundarySelectType.HoverIdle : state.selectType;

      return {
        ...state,
        selectionModeData: {
          ...state.selectionModeData,
          selectedBucketId,
        },
        selectType: nextSelectType,
      };
    }
    return state;
  }

  if (boundarySelectQuickEditCustomBoundaryOpenModal.match(action)) {
    return {
      ...state,
      ...defaultBoundarySelectOptions,
      isModalOpen: true,
      isActive: false,
      selectBoundaryGroupId: action.payload.selectBoundaryGroupId,
      selectionModeData: {
        type: BoundarySelectMode.QuickEditCustomBoundary,
        action: BoundaryEditAction.EditBoundaries,
        boundaryTerritoryGroupId: action.payload.boundaryTerritoryGroupId,
        buckets: action.payload.buckets,
        selectedBucketId: action.payload.preselectedBucketId ?? null,
      },
      selectedBoundaryInfos: [],
      onSubmit: action.payload.onSubmit,
      onClose: action.payload.onClose,
    };
  }

  switch (action.type) {
    case BOUNDARY_SELECT_EDIT_CLOSE_MODAL: {
      return initialState;
    }

    case BOUNDARY_TERRITORY_GROUP_REMOVE_SUCCESS: {
      const { boundaryTerritoryGroups: removedBTGs } = action.payload;
      if (removedBTGs.some(btg => btg.boundaryGroupId === state.selectBoundaryGroupId)) {
        return initialState;
      }
      return state;
    }

    case BOUNDARY_SELECT_EDIT_DEACTIVATE: {
      if (!state.isModalOpen) {
        return state;
      }

      return {
        ...state,
        isActive: false,
        selectedBoundaryInfos: [],
      };
    }

    case BOUNDARY_SELECT_EDIT_ACTIVATE: {
      if (!state.isModalOpen) {
        return state;
      }

      return {
        ...state,
        isActive: true,
      };
    }

    case BOUNDARY_SELECT_EDIT_RESET: {
      if (!state.isModalOpen) {
        return state;
      }

      return {
        ...state,
        isActive: false,
        selectType: state.selectType === BoundarySelectType.HoverActive ? BoundarySelectType.HoverIdle : state.selectType,
        selectedBoundaryInfos: [],
      };
    }

    case BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_SUBMIT_OPEN_MODAL: {
      return {
        ...state,
        ...defaultBoundarySelectOptions,
        isModalOpen: true,
        isActive: false,
        allowMultipleSelection: false,
        selectBoundaryGroupId: action.payload.selectBoundaryGroupId,
        selectionModeData: {
          type: BoundarySelectMode.EditCustomBoundarySubmit,
          boundaryTerritoryGroupId: action.payload.boundaryTerritoryGroupId,
          configuration: {
            ...action.payload.configuration,
          },
        },
        selectedBoundaryInfos: [],
        onSubmit: action.payload.onSubmit,
        onClose: action.payload.onClose,
      };
    }

    case BOUNDARY_SELECT_EDIT_BOUNDARY_TERRITORY_OPEN_MODAL: {
      return {
        ...state,
        ...defaultBoundarySelectOptions,
        isModalOpen: true,
        isActive: false,
        selectBoundaryGroupId: action.payload.selectBoundaryGroupId,
        selectionModeData: {
          type: BoundarySelectMode.EditBoundaryTerritory,
          boundaryTerritoryGroupId: action.payload.boundaryTerritoryGroupId,
        },
        selectedBoundaryInfos: [],
        onSubmit: action.payload.onSubmit,
        onClose: action.payload.onClose,
      };
    }

    case BOUNDARY_SELECT_EDIT_CUSTOM_BOUNDARY_OPEN_MODAL: {
      return {
        ...state,
        ...defaultBoundarySelectOptions,
        isModalOpen: true,
        isActive: false,
        selectBoundaryGroupId: action.payload.selectBoundaryGroupId,
        selectionModeData: {
          type: BoundarySelectMode.EditCustomBoundary,
          action: action.payload.actionType,
          boundaryTerritoryGroupId: action.payload.boundaryTerritoryGroupId,
          boundaryId: action.payload.boundaryId,
        },
        selectedBoundaryInfos: [],
        onSubmit: action.payload.onSubmit,
        onClose: action.payload.onClose,
      };
    }

    case BOUNDARY_SELECT_SET_LASSO_IS_LOADING: {
      if (!state.isModalOpen) {
        return state;
      }

      return {
        ...state,
        isLassoSelectLoading: action.payload.isLoading,
      };
    }

    case BOUNDARY_SELECT_SET_LASSO_OVERLAP: {
      if (!state.isModalOpen) {
        return state;
      }

      return {
        ...state,
        selectLassoOverlap: action.payload.overlap,
      };
    }

    case BOUNDARY_SELECT_WMS_OPTIMIZED_TERRITORIES_SUBSET_OPEN_MODAL: {
      const {
        boundaryGroupId,
        configuration,
      } = action.payload;

      const intermediateState = {
        ...state,
        isModalOpen: true,
        isActive: false,
        selectBoundaryGroupId: boundaryGroupId,
        selectionModeData: {
          type: BoundarySelectMode.CreateWMSOptimizedTerritoriesSubset,
          configuration,
        },
        selectedBoundaryInfos: [],
        hoveredBoundaryId: null,
        onSubmit: action.payload.onSubmit,
        onClose: action.payload.onClose,
      } satisfies BoundarySelectState;

      return {
        ...intermediateState,
        selectedBoundaryInfos: action.payload.selectedBoundaryIds?.map(boundaryId => {
          return {
            boundaryId,
            colorInfo: getSelectionColor(intermediateState, boundaryId),
          };
        }) ?? [],
      };
    }

    case BOUNDARY_SELECT_OPTIMIZED_TERRITORIES_SUBSET_OPEN_MODAL: {
      const {
        boundaryGroupId,
        configuration,
      } = action.payload;

      const intermediateState = {
        ...state,
        isModalOpen: true,
        isActive: false,
        selectBoundaryGroupId: boundaryGroupId,
        selectionModeData: {
          type: BoundarySelectMode.CreateOptimizedTerritoriesSubset,
          configuration,
        },
        selectedBoundaryInfos: [],
        hoveredBoundaryId: null,
        onSubmit: action.payload.onSubmit,
        onClose: action.payload.onClose,
      } satisfies BoundarySelectState;

      return {
        ...intermediateState,
        selectedBoundaryInfos: action.payload.selectedBoundaryIds?.map(boundaryId => {
          return {
            boundaryId,
            colorInfo: getSelectionColor(intermediateState, boundaryId),
          };
        }) ?? [],
      };
    }

    case BOUNDARY_SELECT_ADD_BOUNDARY: {
      if (!isBoundarySelectActive(state) || !notEmpty(action.payload.boundaryIds)) {
        return state;
      }
      if (state.allowMultipleSelection) {
        const colorInfo = getSelectionColor(state, action.payload.selectedBucketId);

        return {
          ...state,
          selectedBoundaryInfos: state.selectedBoundaryInfos
            .filter(boundaryInfo => !action.payload.boundaryIds.includes(boundaryInfo.boundaryId))
            .concat(action.payload.boundaryIds.map(boundaryId => {
              return {
                boundaryId,
                colorInfo,
                selectedBucketId: action.payload.selectedBucketId ?? undefined,
              } satisfies ArrayElement<typeof state.selectedBoundaryInfos>;
            })),
        };
      }
      else {
        const firstBoundaryId: number = action.payload.boundaryIds[0];
        const newSelectBoundaryIds = [{
          boundaryId: firstBoundaryId,
          colorInfo: getSelectionColor(state, firstBoundaryId),
        }];

        return {
          ...state,
          selectedBoundaryInfos: newSelectBoundaryIds,
        };
      }
    }

    case BOUNDARY_SELECT_REMOVE_BOUNDARY: {
      if (!isBoundarySelectActive(state)) {
        return state;
      }

      const newSelectBoundaryIds = state.selectedBoundaryInfos
        .filter(info => info.boundaryId !== action.payload.boundaryId);

      return {
        ...state,
        selectedBoundaryInfos: newSelectBoundaryIds,
      };
    }

    case BOUNDARY_SELECT_HOVER_BOUNDARY: {
      if (!isBoundarySelectActive(state)) {
        return state;
      }

      return {
        ...state,
        hoveredBoundaryId: action.payload.boundaryId,
      };
    }

    case BOUNDARY_SELECT_STOP_BOUNDARY_HOVER: {
      return {
        ...state,
        hoveredBoundaryId: null,
      };
    }

    case BOUNDARY_SELECT_SET_SELECT_TYPE: {
      return {
        ...state,
        selectType: action.payload.selectType,
      };
    }

    default:
      return state;
  }
};
